/**
 * Lightweight SEO hook — updates document <head> tags reactively.
 *
 * Why an in-house hook instead of react-helmet-async:
 *   - Zero dependency cost
 *   - We can deterministically reset to the site-wide defaults (in
 *     `public/index.html`) on unmount, which Helmet doesn't always do
 *     cleanly when navigating between SPA routes
 *
 * Crawlers that DON'T render JS (LinkedIn, Slack, Discord, sometimes Twitter)
 * will see the static defaults in `public/index.html` instead. That's by
 * design — the static defaults are intentionally generic-but-on-brand so
 * shares always look clean.
 */
import { useEffect } from "react";

const PROD_DOMAIN = "https://feelgreats.co.uk";

const DEFAULTS = Object.freeze({
  title: "Smart Health Assessment & Personalized Wellness Plan | Feel Greats",
  description:
    "Take a 3-minute health assessment to receive a personalized wellness plan, metabolism insights, and tailored wellness support from Feel Greats.",
  ogImage: `${PROD_DOMAIN}/og-default.jpg`,
  ogType: "website",
  twitterCard: "summary_large_image",
  siteName: "Feel Greats",
});

// DB-backed per-route SEO overrides (set via /admin → SEO → Metadata).
// Cache is pinned to `window` so HMR / multiple module instances never
// produce stale reads. Populated once on app boot by loadSeoOverrides();
// re-fetched manually via reloadSeoOverrides() after an admin save.
// Track the most-recently-applied page opts so loadSeoOverrides() can
// re-apply them once the cache lands, regardless of which page is mounted.
// More reliable than relying on every mounted useSeo to register its own
// listener (React StrictMode double-invocation + race with fetch made the
// listener pattern flaky on first paint).
let _lastOpts = null;

function _setLastOpts(opts) { _lastOpts = opts; }
function _getLastOpts() { return _lastOpts; }

const OVERRIDE_WINDOW_KEY = "__fgSeoOverrides_v1";

function _getCache() {
  if (typeof window === "undefined") return {};
  return window[OVERRIDE_WINDOW_KEY] || {};
}

function _setCache(obj) {
  if (typeof window === "undefined") return;
  window[OVERRIDE_WINDOW_KEY] = obj || {};
}

let _overrideLoadPromise = null;

export async function loadSeoOverrides() {
  if (typeof window === "undefined") return;
  if (_overrideLoadPromise) return _overrideLoadPromise;
  _overrideLoadPromise = (async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${base}/api/seo/overrides`, { credentials: "omit" });
      if (!r.ok) return;
      const data = await r.json();
      _setCache(data.overrides || {});
      // Re-apply head with the most-recent page opts so the override lands
      // even if the page rendered BEFORE the fetch resolved.
      try {
        const opts = _getLastOpts();
        if (opts !== null) applyMeta(opts || {});
      } catch { /* ignore */ }
      // Also fire the event for any code that wants to react.
      window.dispatchEvent(new CustomEvent("fg:seo-overrides-loaded"));
    } catch { /* analytics must never break the app */ }
  })();
  return _overrideLoadPromise;
}

export async function reloadSeoOverrides() {
  _overrideLoadPromise = null;
  return loadSeoOverrides();
}

/**
 * Set or update a head meta tag, creating it if it doesn't exist yet.
 * `selector` is the CSS selector that uniquely identifies the tag.
 */
function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    // selector e.g. 'meta[property="og:title"]' or 'meta[name="description"]'
    const m = selector.match(/\[([^=]+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Hreflang alternates — one <link rel="alternate" hreflang="..."> per
// supported locale. We always set x-default too. Reusing data-fg-hreflang
// makes cleanup on route change trivial.
const SEO_SUPPORTED_LOCALES = ["en", "hi", "fr", "ar"];

function currentSiteLocale() {
  if (typeof window === "undefined") return "en";
  try {
    const q = window.location.search.match(/[?&]lang=([a-zA-Z-]{2,8})/);
    if (q) {
      const code = q[1].toLowerCase().split("-")[0];
      if (SEO_SUPPORTED_LOCALES.includes(code)) return code;
    }
    const stored = (localStorage.getItem("fg_locale") || "").toLowerCase().split("-")[0];
    if (stored && SEO_SUPPORTED_LOCALES.includes(stored)) return stored;
  } catch { /* ignore */ }
  return "en";
}

/** Build the public canonical URL for a given pathname + locale. */
function localeCanonical(pathname, locale) {
  if (!locale || locale === "en") return `${PROD_DOMAIN}${pathname}`;
  return `${PROD_DOMAIN}${pathname}?lang=${locale}`;
}

/** Append ?lang=<code> (or &lang=) to an explicit canonical URL when the
 *  active locale isn't English and the URL doesn't already carry a
 *  `lang=` query param. Pages can keep hard-coding canonical safely. */
function withLocaleQuery(url, locale) {
  if (!url || !locale || locale === "en") return url;
  if (/[?&]lang=/.test(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}lang=${locale}`;
}

function setHreflangAlternates(pathname) {
  document.head
    .querySelectorAll('link[data-fg-hreflang]')
    .forEach((n) => n.parentNode && n.parentNode.removeChild(n));
  for (const code of SEO_SUPPORTED_LOCALES) {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", code);
    el.setAttribute("href", localeCanonical(pathname, code));
    el.setAttribute("data-fg-hreflang", code);
    document.head.appendChild(el);
  }
  const xd = document.createElement("link");
  xd.setAttribute("rel", "alternate");
  xd.setAttribute("hreflang", "x-default");
  xd.setAttribute("href", `${PROD_DOMAIN}${pathname}`);
  xd.setAttribute("data-fg-hreflang", "x-default");
  document.head.appendChild(xd);
}

/**
 * Apply a full set of head tags. Pass null to reset to site-wide defaults.
 */
function applyMeta({ title, description, canonical, ogImage, ogType, twitterCard, type, jsonLd }) {
  // Per-route DB overrides (set via /admin → SEO → Metadata). Loaded once on
  // app boot via loadSeoOverrides(); read from `window` for HMR stability.
  const cache = _getCache();
  const pathOverride = (typeof window !== "undefined") ? (cache[window.location.pathname] || {}) : {};
  const T = pathOverride.title || title || DEFAULTS.title;
  const D = pathOverride.description || description || DEFAULTS.description;
  const I = ogImage || DEFAULTS.ogImage;
  const OT = ogType || type || DEFAULTS.ogType;
  const TC = twitterCard || DEFAULTS.twitterCard;
  // Locale-aware canonical: include ?lang=XX for non-English so Google
  // doesn't treat the localised variant as a duplicate of the root URL.
  // Pages can still pass an explicit `canonical` — we'll layer the
  // locale query on top of it.
  const activeLocale = currentSiteLocale();
  const C = pathOverride.canonical
    ? withLocaleQuery(pathOverride.canonical, activeLocale)
    : (canonical
        ? withLocaleQuery(canonical, activeLocale)
        : localeCanonical(window.location.pathname, activeLocale));

  document.title = T;
  setMeta('meta[name="description"]', "content", D);
  setLink("canonical", C);
  setHreflangAlternates(window.location.pathname);

  // og:locale matches the active locale variant — helps social previews
  // pick the right script (e.g. Arabic RTL fonts).
  const ogLocaleMap = { en: "en_GB", fr: "fr_FR", hi: "hi_IN", ar: "ar_AE" };
  setMeta('meta[property="og:locale"]', "content", ogLocaleMap[activeLocale] || "en_GB");

  // Open Graph (per-route override > page > default)
  const OG_T = pathOverride.og_title || T;
  const OG_D = pathOverride.og_description || D;
  setMeta('meta[property="og:title"]', "content", OG_T);
  setMeta('meta[property="og:description"]', "content", OG_D);
  setMeta('meta[property="og:type"]', "content", OT);
  setMeta('meta[property="og:url"]', "content", C);
  setMeta('meta[property="og:image"]', "content", I);
  setMeta('meta[property="og:image:secure_url"]', "content", I);
  setMeta('meta[property="og:site_name"]', "content", DEFAULTS.siteName);

  // Twitter
  setMeta('meta[name="twitter:card"]', "content", TC);
  setMeta('meta[name="twitter:title"]', "content", OG_T);
  setMeta('meta[name="twitter:description"]', "content", OG_D);
  setMeta('meta[name="twitter:image"]', "content", I);

  // Optional JSON-LD structured data — replaces any previous "page" blocks
  // we set. Supports an ARRAY of LD objects (each becomes its own
  // <script>) so multiple @type entries (Product + BreadcrumbList +
  // FAQPage etc.) are detectable by crawlers that parse one block at a
  // time. A single object is also accepted for backwards compat.
  document.head.querySelectorAll('script[data-fg-jsonld="page"]').forEach((n) => n.remove());
  if (jsonLd) {
    const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    arr.forEach((obj) => {
      if (!obj) return;
      const el = document.createElement("script");
      el.setAttribute("type", "application/ld+json");
      el.setAttribute("data-fg-jsonld", "page");
      el.textContent = JSON.stringify(obj);
      document.head.appendChild(el);
    });
  }
}

/**
 * useSeo — set head tags for a page. Reverts to site-wide defaults on unmount.
 */
export default function useSeo(opts) {
  useEffect(() => {
    // Snapshot opts at module level so loadSeoOverrides() can re-apply once
    // the override cache lands — race-proof against fetch timing on first
    // paint (React StrictMode double-invocation + fast fetch made the
    // event-listener approach unreliable).
    _setLastOpts(opts || null);
    applyMeta(opts || {});
    return () => {
      _setLastOpts(null);
      applyMeta({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    opts?.title,
    opts?.description,
    opts?.canonical,
    opts?.ogImage,
    opts?.ogType,
    opts?.type,
    opts?.twitterCard,
    JSON.stringify(opts?.jsonLd || null),
  ]);
}

export { PROD_DOMAIN };
